import { EpgDetailDataSource } from './EpgDetailDataSource';
import { EpgUtil, ISortedProgram, ISortedSeasons } from '../../utility/EpgUtil';
import { AssetType } from '../../../api/models/Shared.types';
import { SharedFavouriteActionButton } from '../../../pages/detail/actionRowButtons/SharedFavouriteActionButton';
import { EpgSeriesDetailDescriptionDataSource } from './SeriesDetailDescriptionDataSource';
import { atvPromiseAll } from '../../utility/PromiseUtil';
import { DetailTranslationKeys, SettingsKeys } from '../../../api/models/TranslationKeys';
import { EpgEpiRibbonData, TabbedEpgEpisodesRibbonDataSource } from '../../cms/TabbedEpgEpisodesRibbonDataSource';
import { BookmarkUtility } from '../../utility/BookmarkUtility';
import { EpgWatchAction } from '../actions/EpgWatchAction';
import { ActionButton } from '../../../pages/detail/actionRowButtons/ActionButton';
import { StaticPageUrls } from '../../../api/analytics/ClientEvents';
import { Backend } from '../../../api/Backend';
import { IBookmarkElementV1, IProgramV5, IScheduleV5, ISeasonV5 } from '../../../api/models/AndromeTV.types';
import { Recording } from '../../../api/user/RecordingApi.types';
import { ChannelModel } from '../../../api/EpgApi.types';
import { IActionButtonDefinition, IDetailModel } from '../detail.types';
import { EpgApiV5 } from '../../../api/EpgApiV5';
import { RibbonDataSource } from '../../cms/RibbonDataSource';
import { TabbedRecordingRibbonDataSource } from '../../cms/TabbedRecordingRibbonDataSource';

export class SeriesDetailDataSource extends EpgDetailDataSource {
  private schedules: IScheduleV5[] | undefined;
  private programs: IProgramV5[] | undefined;
  private seasons: ISeasonV5[] | undefined;
  private seriesId: string | undefined;

  constructor(backend: Backend) {
    super(backend);
    this.series = undefined;
    this.schedules = undefined;
    this.programs = undefined;
  }

  override logPageView() {
    this.backend.logService.trackPageView(StaticPageUrls.epgSeriesDetail);
  }

  override load(seriesId: string): Promise<IDetailModel | undefined> {
    this.seriesId = seriesId;
    const fromUntil = EpgUtil.epgFromUntil(this.backend);

    return Promise.all([
      EpgApiV5.series(this.backend, seriesId),
      EpgApiV5.seriesLineup(this.backend, seriesId, fromUntil.from, fromUntil.until),
      this.initChannels(),
    ]).then(([seriesDetail, seriesLineup, _channels]) => {
      //todo check adult
      if (seriesDetail && seriesLineup) {
        this.series = seriesDetail.series;
        this.schedules = seriesLineup.schedules;
        this.programs = seriesLineup.programs?.sort((a, b) => {
          if (!a.episodeNumber || !b.episodeNumber) return 0;
          if (a.episodeNumber > b.episodeNumber) {
            return 1;
          } else if (a.episodeNumber < b.episodeNumber) {
            return -1;
          } else {
            return 0;
          }
        });
        this.seasons = seriesLineup.seasons;
        return this.getDetailData();
      }
      return undefined;
    });
  }

  getDetailData(): Promise<IDetailModel | undefined> {
    return atvPromiseAll([this.getRibbons(), this.getNextEpisode()]).then(([ribbons, nextEpisodeInfo]) => {
      if (this.series) {
        const res: IDetailModel = {
          description: new EpgSeriesDetailDescriptionDataSource(this.seriesId, this.series, this.backend),
          images: {
            detailPoster: this.series?.image.poster,
          },
          buttons: this.getButtons(nextEpisodeInfo),
          ribbonDatasources: ribbons,
        };
        return res;
      }
    });
  }

  override getRibbons() {
    const id = this.series ? this.series.seriesId : this.program?.programId;
    const type = this.series ? AssetType.SERIES : AssetType.PROGRAM;

    let result: (RibbonDataSource | TabbedEpgEpisodesRibbonDataSource | TabbedRecordingRibbonDataSource)[] = [];

    const data: EpgEpiRibbonData = {
      series: this.series,
      seasons: this.seasons,
      programs: this.programs,
      schedules: this.schedules,
    };

    if (this.series && this.series.seriesId && this.channelsList) {
      const episodesRibbon = TabbedEpgEpisodesRibbonDataSource.fromData(
        this.backend,
        this.series.seriesId,
        this.backend.translations.forKey(DetailTranslationKeys.detail_btn_all_episodes),
        this.channelsList,
        data
      );
      result.push(episodesRibbon);
    }
    if (id) result = result.concat(this.createRelatedDataSources(this.backend, type, id, true, this.channelsList));

    return this.getRecordingRibbon().then(rib => {
      if (rib) result.splice(0, 0, rib);
      return result;
    });
  }

  override getButtons(nextEpisodeInfo: ISortedProgram): IActionButtonDefinition[] {
    const result: IActionButtonDefinition[] = [];

    if (
      nextEpisodeInfo &&
      this.backend.globalSettings.boolValue(SettingsKeys.showSeriesWatchButton) &&
      this.series &&
      this.channelsList
    ) {
      const actionHandler = new EpgWatchAction(
        this.backend,
        AssetType.SERIES,
        new ChannelModel(nextEpisodeInfo.channel),
        nextEpisodeInfo.schedule,
        nextEpisodeInfo.program,
        this.series,
        nextEpisodeInfo.season,
        undefined,
        nextEpisodeInfo.program.adult,
        this.channelsList,
        true
      );

      result.push({
        type: ActionButton,
        actionHandler,
        label: this.backend.translations.forKey(DetailTranslationKeys.detail_btn_watch_now),
      });
    }

    if (this.backend.isAuthorized()) {
      result.push({
        type: SharedFavouriteActionButton,
        assetId: this.series?.seriesId,
        assetType: AssetType.SERIES,
        assetIsAdult: this.series?.adult,
      });
    }

    return result;
  }

  getNextEpisode(): Promise<ISortedProgram | undefined> {
    let showSeriesWatchButton = this.backend.globalSettings.boolValue(SettingsKeys.showSeriesWatchButton);
    showSeriesWatchButton = showSeriesWatchButton ? showSeriesWatchButton : false;
    if (!showSeriesWatchButton) {
      return Promise.resolve(undefined);
    }
    if (!this.backend.isAuthorized()) return Promise.resolve(undefined);

    if (!this.seriesId) return Promise.resolve(undefined);
    return atvPromiseAll([
      this.backend.cachedBookmarkApi.seriesBookmarks(this.backend, this.seriesId, AssetType.PROGRAM),
      this.backend.cachedRecordingsApi.recordingsForSeries(this.backend, this.seriesId),
    ]).then(([bookmarks, recordings]) => {
      const sortedPrograms = EpgUtil.sortProgramsPerSeason(
        this.backend,
        this.seasons,
        this.programs,
        this.schedules,
        this.channelsList,
        this.series
      );

      let nextProgramForSeason = undefined;

      // try to find next epi from the last bookmark
      if (bookmarks && bookmarks.length > 0) {
        const latestBookmark = BookmarkUtility.getLatestBookmark(bookmarks);

        if (latestBookmark && this.programs) {
          nextProgramForSeason = this.nextEpisodeFromBookmark(
            latestBookmark,
            sortedPrograms,
            this.programs,
            recordings
          );
        }
      }

      // if none from bookmark, take the first episode of the series
      if (!nextProgramForSeason) {
        nextProgramForSeason = this.getFirstEpiOfFirstSeason(sortedPrograms, recordings);
      }

      return nextProgramForSeason;
    });
  }

  nextEpisodeFromBookmark(
    bookmark: IBookmarkElementV1,
    sortedProgramsPerSeason: ISortedSeasons[],
    programs: IProgramV5[],
    recordings: Recording[]
  ): ISortedProgram | undefined {
    const progIndex = programs.findIndex(prog => prog.programId === bookmark.id);

    if (progIndex >= 0) {
      const lastProgram = programs[progIndex];
      const sortedSeason = sortedProgramsPerSeason.find(season => season.seasonId === lastProgram?.seasonId);
      if (BookmarkUtility.isFullyWatched(this.backend, bookmark)) {
        // If already last episode → no watch button
        if (progIndex < programs.length - 1 && lastProgram && sortedSeason) {
          return this.getNextProgram(lastProgram, sortedSeason, recordings);
        }
      } else {
        const tempNextProgram = sortedSeason?.sortedPrograms.find(
          sortedProgram => sortedProgram.program.programId === lastProgram?.programId
        );
        if (
          tempNextProgram &&
          (EpgUtil.canWatchReplay(tempNextProgram.schedule, new ChannelModel(tempNextProgram.channel)) ||
            this.hasWatchableRecording(tempNextProgram, recordings))
        ) {
          return tempNextProgram;
        }
      }
    }
    return undefined;
  }

  getNextProgram(
    lastProgram: IProgramV5,
    sortedSeason: ISortedSeasons,
    recordings: Recording[]
  ): ISortedProgram | undefined {
    let selectedNextProgram;

    if (sortedSeason) {
      const lastProgramIndex = sortedSeason.sortedPrograms.findIndex(
        sortedProgram => sortedProgram.program.programId === lastProgram.programId
      );

      //  IF LAST PROGRAM HAS AN EPISODE NUMBER
      if (lastProgram.episodeNumber !== undefined) {
        // FIND FIRST EPISODE WITH A GREATER NUMBER
        const nextEpisodeNumber = sortedSeason.sortedPrograms.find(
          sortedProgram => (sortedProgram.program.episodeNumber ?? 0) > (lastProgram.episodeNumber ?? 0)
        );

        if (nextEpisodeNumber) {
          // SELECT ALL EPISODE IN SEASON WITH SAME EPISODE NUMBER
          const nextEpisodePrograms = sortedSeason.sortedPrograms.filter(
            sortedProgram => sortedProgram.program.episodeNumber === nextEpisodeNumber.program.episodeNumber
          );

          // FIND A REPLAYABLE PROGRAM
          selectedNextProgram = nextEpisodePrograms.find(
            sortedProgram =>
              EpgUtil.canWatchReplay(sortedProgram.schedule, new ChannelModel(sortedProgram.channel)) ||
              this.hasWatchableRecording(sortedProgram, recordings)
          );
        }
      }

      // IF EPISODE OF LAST SEASON IS UNDEFINED, SELECT NEXT EPISODE IN ARRAY
      if (
        lastProgram.episodeNumber === undefined &&
        lastProgramIndex >= 0 &&
        lastProgramIndex + 1 < sortedSeason.sortedPrograms.length - 1
      ) {
        // CHECK IF IS REPLAYABLE
        const tempNextProgram = sortedSeason.sortedPrograms[lastProgramIndex + 1];
        if (tempNextProgram && tempNextProgram.channel) {
          selectedNextProgram = EpgUtil.canWatchReplay(
            tempNextProgram.schedule,
            new ChannelModel(tempNextProgram.channel)
          )
            ? tempNextProgram
            : undefined;
        }
      }
    }

    return selectedNextProgram;
  }

  hasWatchableRecording(program: ISortedProgram, recordings: Recording[]) {
    if (recordings && program.channel && program.schedule && program.program) {
      const bookmarkRec = recordings.find(
        recording =>
          recording.channel === program.channel.channelId &&
          recording.program === program.program.programId &&
          recording.schedule === program.schedule.scheduleId
      );
      return bookmarkRec
        ? EpgUtil.canPlayRecording(this.backend, bookmarkRec, program.schedule, new ChannelModel(program.channel))
        : false;
    }

    return false;
  }

  getFirstEpiOfFirstSeason(
    sortedProgramsPerSeason: ISortedSeasons[],
    recordings: Recording[]
  ): ISortedProgram | undefined {
    if (sortedProgramsPerSeason && sortedProgramsPerSeason.length > 0) {
      const season = sortedProgramsPerSeason[0];

      if (season && season.sortedPrograms && season.sortedPrograms.length > 0) {
        const tempNextProgram = season.sortedPrograms[0];
        if (!tempNextProgram) return undefined;
        return EpgUtil.canWatchReplay(tempNextProgram.schedule, new ChannelModel(tempNextProgram.channel)) ||
          this.hasWatchableRecording(tempNextProgram, recordings)
          ? tempNextProgram
          : undefined;
      }
    }

    return undefined;
  }
}
